import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom-v5-compat";
import * as z from "zod";

import { getJWTToken } from "./Authentication";
import { useBrand } from "./BrandContext";
import ErrorBoundary from "./ErrorBoundary";
import { VenueErrorPage } from "./ErrorPage";
import OnlineMenuMessage from "./OnlineMenuMessage";
import { SessionProvider, useSession } from "./Session/SessionContext";
import { getIsWishlist } from "./Session/sessionSelectors";
import { useVenue } from "./VenueContext";
import { useRecordFeatureUsage } from "./queries";
import { getVenueHasMobileOrdering } from "./venueSelectors";

const ten_minutes = 1000 * 60 * 10;
const SeenMenuMessagesSchema = z.record(z.string(), z.number());
const getHasSeenMessage = () =>
  SeenMenuMessagesSchema.parse(
    JSON.parse(localStorage.getItem("seenMenuMessages") || "{}"),
  );

const getHasSeenMessageInitialState = (currentMenuMessageTitle: string) => {
  const seenMenuMessages = getHasSeenMessage();
  return seenMenuMessages[currentMenuMessageTitle] > Date.now() - ten_minutes;
};
export const VenueLayout = () => {
  const brand = useBrand();
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const isWishlist = getIsWishlist(useSession(), useVenue());
  const { venueBrand } = useVenue();
  const { t } = useTranslation();
  const jwtToken = getJWTToken();

  const activeMenuMessage = venueBrand.activeMenuMessage;
  const [seenMenuMessage, setMenuMessage] = useState(
    activeMenuMessage
      ? getHasSeenMessageInitialState(activeMenuMessage.title)
      : true,
  );

  const recordFeatureUsage = useRecordFeatureUsage();
  const dismissMessage = () => {
    recordFeatureUsage.mutate({
      featureName: "Online / Menu Message",
      usageType: "engagement",
    });

    setMenuMessage(true);
    if (!activeMenuMessage) return;
    const seenMenuMessages = getHasSeenMessage();
    localStorage.setItem(
      "seenMenuMessages",
      JSON.stringify({
        ...seenMenuMessages,
        [activeMenuMessage.title]: Date.now(),
      }),
    );
  };
  return (
    <div
      style={brand.cssVariables}
      className="text-venue-primary bg-venue-secondary"
    >
      {!seenMenuMessage && (
        <div className="flex flex-col justify-center z-20 fixed inset-0 items-stretch ">
          <div className="bg-venue-tertiary flex fixed inset-0 opacity-60" />
          <div className="flex items-stretch z-10 px-32">
            <OnlineMenuMessage onDismiss={dismissMessage} />
          </div>
        </div>
      )}
      <div className="mx-auto min-h-screen max-w-screen-xl md:px-32 px-16">
        <ErrorBoundary
          render={(message, resetError) => (
            <VenueErrorPage resetError={resetError} errorMessage={message} />
          )}
        >
          <SessionProvider>
            <Outlet />
          </SessionProvider>
        </ErrorBoundary>
      </div>
    </div>
  );
};
